import React, { useEffect, useRef, useState } from 'react';

import isEqual from 'react-fast-compare';

import DragHandleVerticalIcon from '@atlaskit/icon/core/migration/drag-handle-vertical--drag-handler';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { scrollJustEnoughIntoView } from '@atlaskit/pragmatic-drag-and-drop/element/scroll-just-enough-into-view';
import Tooltip from '@atlaskit/tooltip';

import {
	ActionsWrapper,
	Card,
	Container,
	Content,
	DragHandlerWrapper,
	Header,
	Title,
	TitleContainer,
	TitleWrapper,
} from './styled';
import { type DraggableState, type RuleCardProps } from './types';

export const RuleCard = <T extends NonNullable<unknown>>({
	className,
	title,
	actions,
	children,
	value,
	droppableId,
	gap = '0px',
	description,
	headingAs = 'h4',
	contentSpacing = 'normal',
}: RuleCardProps<T>) => {
	const titleRef = useRef<HTMLHeadingElement>(null);
	const ruleCardRef = useRef<HTMLDivElement>(null);
	const handlerRef = useRef<HTMLDivElement>(null);
	const [isEllipsis, setIsEllipsis] = useState(false);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [dragState, setDragState] = useState<DraggableState>('idle');

	useEffect(() => {
		if (!titleRef.current) {
			return;
		}

		const el = titleRef.current;
		setIsEllipsis(el.offsetHeight < el.scrollHeight || el.offsetWidth < el.scrollWidth);
	}, []);

	useEffect(() => {
		if (!ruleCardRef.current || !handlerRef.current || !droppableId) {
			return;
		}

		return combine(
			draggable({
				element: ruleCardRef.current,
				dragHandle: handlerRef.current,
				getInitialData: () => ({ value, droppableId }),
				onGenerateDragPreview: ({ source }) => {
					scrollJustEnoughIntoView({ element: source.element });
					setDragState('generate-preview');
				},
				onDragStart: () => setDragState('dragging'),
				onDrop: () => setDragState('idle'),
			}),
			dropTargetForElements({
				element: ruleCardRef.current,
				canDrop: ({ source: { data } }) => data.droppableId === droppableId,
				getIsSticky: () => true,
				getData: ({ input, element }) => {
					const data = { value, droppableId };

					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				onDrop: (args) => {
					setClosestEdge(null);
				},
				onDragEnter: (args) => {
					if (!isEqual(args.self.data.value, args.source.data.value)) {
						setClosestEdge(extractClosestEdge(args.self.data));
					}
				},
				onDragLeave: (args) => {
					setClosestEdge(null);
				},
				onDrag: (args) => {
					if (!isEqual(args.self.data.value, args.source.data.value)) {
						setClosestEdge(extractClosestEdge(args.self.data));
					}
				},
			}),
		);
	}, [droppableId, value]);

	return (
		<Card state={dragState}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<Container ref={ruleCardRef} className={className}>
				{(title || droppableId || actions) && (
					<Header>
						<TitleContainer>
							{droppableId && (
								<DragHandlerWrapper data-testid="rule-card-drag-handler" ref={handlerRef}>
									<DragHandleVerticalIcon color="currentColor" spacing="spacious" label="" />
								</DragHandlerWrapper>
							)}
							{title && (
								<TitleWrapper>
									<Tooltip content={isEllipsis ? title : undefined}>
										<Title as={headingAs} ref={titleRef}>
											{title}
										</Title>
									</Tooltip>
								</TitleWrapper>
							)}

							{description}
						</TitleContainer>

						{actions && <ActionsWrapper>{actions}</ActionsWrapper>}
					</Header>
				)}
				<Content contentSpacing={contentSpacing}>{children}</Content>
			</Container>
			{closestEdge && <DropIndicator edge={closestEdge} gap={gap} type="no-terminal" />}
		</Card>
	);
};

export default RuleCard;
